import { Button, Typography } from "@joonapp/web-shared"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { CloseButton } from "../../../components/closeButton/CloseButton"
import SlideUpModal from "../../../components/slideUpModal/SlideUpModal"
import { ScreenTimeRestrictionsConfirmationModalType } from "../../../types/rewards"

interface ScreenTimeRestrictionsConfirmationModalStore {
  isOpen: boolean
  onOpen: (type: ScreenTimeRestrictionsConfirmationModalType) => void
  onClose: () => void
  type: ScreenTimeRestrictionsConfirmationModalType
  setType: (type: ScreenTimeRestrictionsConfirmationModalType) => void
}

export const useScreenTimeRestrictionsConfirmationModalStore =
  create<ScreenTimeRestrictionsConfirmationModalStore>((set) => ({
    isOpen: false,
    onOpen: (type) => set({ isOpen: true, type }),
    onClose: () => set({ isOpen: false }),
    type: ScreenTimeRestrictionsConfirmationModalType.SCHEDULE,
    setType: (type: ScreenTimeRestrictionsConfirmationModalType) =>
      set({ type }),
  }))

const ScreenTimeRestrictionsConfirmationModal = () => {
  const { isOpen, onClose, type } =
    useScreenTimeRestrictionsConfirmationModalStore()
  const { t } = useTranslation()

  const titleText = useMemo(() => {
    switch (type) {
      case ScreenTimeRestrictionsConfirmationModalType.SCHEDULE:
        return t("STRCM_SCHEDULE_TITLE")
      case ScreenTimeRestrictionsConfirmationModalType.TURN_OFF:
        return t("STRCM_TURN_OFF_TITLE")
      case ScreenTimeRestrictionsConfirmationModalType.TURN_ON:
        return t("STRCM_TURN_ON_TITLE")
    }
  }, [type, t])

  const descriptionText = useMemo(() => {
    switch (type) {
      case ScreenTimeRestrictionsConfirmationModalType.SCHEDULE:
        return t("STRCM_SCHEDULE_DESC")
      case ScreenTimeRestrictionsConfirmationModalType.TURN_OFF:
        return t("STRCM_TURN_OFF_DESC")
      case ScreenTimeRestrictionsConfirmationModalType.TURN_ON:
        return t("STRCM_TURN_ON_DESC")
    }
  }, [type, t])

  const imageSrc = useMemo(() => {
    if (type === ScreenTimeRestrictionsConfirmationModalType.TURN_ON)
      return "/images/resync_locked_apps.png"
    return "/images/free_screen_time.png"
  }, [type])

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      backgroundType="overlay"
      size="fit-content"
    >
      <div className="w-full flex flex-col align-start p-4 gap-4 bg-bgXLightGray">
        <CloseButton onClick={onClose} />
        <Typography variant="bodyBold">{titleText}</Typography>
        <Typography variant="bodySmall" className="whitespace-pre-line">
          {descriptionText}
        </Typography>
        <img
          src={imageSrc}
          alt="Screen Time Restrictions"
          className={`h-auto ${
            type === ScreenTimeRestrictionsConfirmationModalType.TURN_ON
              ? "w-[70%]"
              : "w-full"
          }`}
        />
        <Button text={t("QUEST_DONE")} onClick={onClose} fullWidth />
      </div>
    </SlideUpModal>
  )
}

export default ScreenTimeRestrictionsConfirmationModal
